import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { QuestionStep } from "@/components/scan/QuestionStep";
import { ScanProgress } from "@/components/scan/ScanProgress";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";

export default function ScanPage() {
  const { sessionId, ready } = useSession();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [runId, setRunId] = useState(localStorage.getItem("a8a_run_id") || null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [initError, setInitError] = useState(null);
  const startInFlight = useRef(false);

  useEffect(() => {
    api.get("/scan/config").then(({ data }) => setConfig(data)).catch((e) => setInitError(apiErrorMessage(e)));
  }, []);

  useEffect(() => {
    const startRun = async () => {
      if (!ready || runId || startInFlight.current) return;
      startInFlight.current = true;
      try {
        const { data } = await api.post("/scan/start", { anonymous_session_id: sessionId });
        setRunId(data.diagnostic_run_id);
        localStorage.setItem("a8a_run_id", data.diagnostic_run_id);
      } catch (e) {
        setInitError(apiErrorMessage(e));
      }
    };
    startRun();
  }, [ready, sessionId, runId]);

  const question = config?.questions?.[step];

  const handleChange = useCallback((value) => {
    setAnswers((prev) => ({ ...prev, [question.code]: value }));
  }, [question]);

  const persistAnswer = async () => {
    if (!question) return;
    const value = answers[question.code];
    if (!value) return;
    setSaving(true);
    try {
      await api.post("/scan/answer", { diagnostic_run_id: runId, question_code: question.code, answer_value: value });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    await persistAnswer();
    if (step + 1 < config.questions.length) {
      setStep(step + 1);
    } else {
      setSaving(true);
      try {
        await api.post("/scan/complete", { diagnostic_run_id: runId });
        localStorage.removeItem("a8a_run_id");
        navigate(`/scan/result/${runId}`);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      } finally {
        setSaving(false);
      }
    }
  };

  const handleBack = () => step > 0 && setStep(step - 1);

  const isRequired = question?.required !== false;
  const canProceed = !isRequired || !!answers[question?.code];

  return (
    <div className="min-h-screen bg-background" data-testid="scan-page">
      <SEO
        title="Free Business Leakage Scan — A8A Diagnostic"
        description="Answer 16 quick questions to uncover revenue, cash, margin and workflow leaks in your business — no software connection required."
      />
      <div className="max-w-2xl mx-auto px-6 py-14">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-foreground leading-tight" data-testid="scan-page-heading">
          Free Business Leakage Scan
        </h1>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed" data-testid="scan-page-intro">
          This short diagnostic asks 16 focused questions across seven business domains — revenue,
          collections &amp; cash, margin, workflow efficiency, operational exceptions, approval
          friction and management visibility. There is nothing to install and no system to connect;
          every question can be answered from what you already know about how the business runs
          day to day. A8A uses your answers to flag the areas most likely to be leaking money, time
          or control, and shows the evidence behind each finding rather than a generic score.
        </p>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
          It usually takes under five minutes. Your answers are saved as you go, so you can stop and
          come back without losing progress. Once complete, you'll see a provisional finding
          immediately — creating a free account afterwards lets A8A tailor the recommendation to
          your specific business and unlock the full opportunity dashboard.
        </p>

        {initError ? (
          <p className="text-destructive mt-10" data-testid="scan-error-state">{initError}</p>
        ) : !config || !runId || !question ? (
          <p className="text-muted-foreground mt-10" data-testid="scan-loading">Loading your scan&hellip;</p>
        ) : (
          <div className="mt-10">
            <ScanProgress current={step} total={config.questions.length} />
            <QuestionStep question={question} value={answers[question.code]} onChange={handleChange} />
            <div className="flex items-center justify-between mt-10">
              <Button variant="ghost" onClick={handleBack} disabled={step === 0} data-testid="scan-back-button">
                Back
              </Button>
              <Button onClick={handleNext} disabled={!canProceed || saving} data-testid="scan-next-button">
                {step + 1 < config.questions.length ? "Continue" : "See My Result"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
